import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getNeonColor } from '../theme';

interface NeonChipProps {
    label: string;
    onClick?: () => void;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'tooltip';
}

export const NeonChip: React.FC<NeonChipProps> = ({ 
    label, 
    onClick, 
    size = 'medium',
    variant = 'default'
}) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const sizeConfig = {
        small: {
            fontSize: '0.65rem',
            height: '22px',
            px: 0.6,
            glowSize: '0.8rem',
            hoverGlowSize: '1rem'
        },
        medium: {
            fontSize: '0.75rem',
            height: '26px',
            px: 0.8,
            glowSize: '1.2rem',
            hoverGlowSize: '1.5rem'
        },
        large: {
            fontSize: '0.85rem',
            height: '30px',
            px: 1,
            glowSize: '1.5rem',
            hoverGlowSize: '1.8rem'
        }
    };

    const config = sizeConfig[size];

    if (variant === 'tooltip') {
        // Tooltip variant - more complex with pseudo-elements
        return (
            <Box
                sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    color: neonColor,
                    fontSize: config.fontSize,
                    height: config.height,
                    fontWeight: 600,
                    px: config.px,
                    cursor: onClick ? 'pointer' : 'default',
                    transition: 'all 0.3s ease-in-out',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(90deg, ${neonColor}30 0%, ${neonColor}10 50%, ${neonColor}30 100%)`,
                        borderRadius: '4px',
                        opacity: 0.3,
                        transition: 'opacity 0.3s ease-in-out'
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -1,
                        left: -1,
                        right: -1,
                        bottom: -1,
                        background: `linear-gradient(45deg, ${neonColor}, transparent, ${neonColor})`,
                        borderRadius: '4px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out',
                        zIndex: -1
                    },
                    '&:hover': {
                        color: '#fff',
                        transform: 'scale(1.08)',
                        '&::before': {
                            opacity: 0.8
                        },
                        '&::after': {
                            opacity: 0.6
                        }
                    }
                }}
                onClick={onClick}
            >
                <Typography
                    sx={{
                        fontSize: config.fontSize,
                        fontWeight: 600,
                        color: 'inherit',
                        textShadow: `0px 0px 8px currentColor`,
                        zIndex: 1,
                        position: 'relative',
                        letterSpacing: '0.5px'
                    }}
                >
                    {label}
                </Typography>
            </Box>
        );
    }

    // Default variant - simple inner glow
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: neonColor,
                borderRadius: '6px',
                boxShadow: `inset 0 0 ${config.glowSize} ${neonColor}60`,
                fontSize: config.fontSize,
                height: config.height,
                fontWeight: 600,
                px: config.px,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                    backgroundColor: `${neonColor}20`,
                    boxShadow: `inset 0 0 ${config.hoverGlowSize} ${neonColor}75`,
                    transition: 'all 0.15s ease-in-out'
                }
            }}
            onClick={onClick}
        >
            <Typography
                sx={{
                    fontSize: config.fontSize,
                    fontWeight: 600,
                    color: 'inherit',
                    letterSpacing: '0.3px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}; 